import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { SalesService } from '../../../../shared/services/apis/platform/sales.service';
import { AdminSalesUploadNfeResponse } from '../../../../shared/services/responses/platform/admin-sales.response';

export interface NfeUploadDialogData {
  idKitPayment: number;
  hasXml: boolean;
  hasDanfe: boolean;
}

/** Anexa XML e DANFE no mesmo envio (ou só um deles). Mantém o aviso de ação não reversível. */
@Component({
  selector: 'app-nfe-upload-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title class="title">Anexar NF-e <span class="muted">· venda #{{ data.idKitPayment }}</span></h2>
    <mat-dialog-content class="content">
      <p class="intro">Envie o XML e o DANFE juntos ou apenas um deles. Número, chave e data de emissão são lidos do XML.</p>

      <div class="drops">
        <div class="drop" [class.has]="xml" [class.done]="data.hasXml && !xml" (click)="xmlInput.click()" (dragover)="$event.preventDefault()" (drop)="onDrop($event, 'xml')" tabindex="0" (keydown.enter)="xmlInput.click()">
          <mat-icon>code</mat-icon>
          <b>NF-e (.xml)</b>
          <span *ngIf="xml">{{ xml.name }} ({{ kb(xml.size) }} KB)</span>
          <span *ngIf="!xml && data.hasXml">já anexado · clique para substituir</span>
          <span *ngIf="!xml && !data.hasXml">Arraste ou clique para escolher</span>
          <input #xmlInput type="file" accept=".xml,text/xml,application/xml" hidden (change)="onPick($event, 'xml')">
        </div>
        <div class="drop" [class.has]="danfe" [class.done]="data.hasDanfe && !danfe" (click)="pdfInput.click()" (dragover)="$event.preventDefault()" (drop)="onDrop($event, 'danfe')" tabindex="0" (keydown.enter)="pdfInput.click()">
          <mat-icon>picture_as_pdf</mat-icon>
          <b>DANFE (.pdf)</b>
          <span *ngIf="danfe">{{ danfe.name }} ({{ kb(danfe.size) }} KB)</span>
          <span *ngIf="!danfe && data.hasDanfe">já anexado · clique para substituir</span>
          <span *ngIf="!danfe && !data.hasDanfe">Arraste ou clique para escolher</span>
          <input #pdfInput type="file" accept="application/pdf,.pdf" hidden (change)="onPick($event, 'danfe')">
        </div>
      </div>

      <div class="note warn">
        <mat-icon>warning</mat-icon>
        <span>Esta ação <b>não é reversível</b>. Após o envio do XML, o worker libera a geração da etiqueta dos Correios (vendas com livro físico).</span>
      </div>
      <div class="error" *ngIf="error">{{ error }}</div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="close()" [disabled]="sending">Cancelar</button>
      <button mat-flat-button class="btn-primary" (click)="send()" [disabled]="(!xml && !danfe) || sending">
        <mat-spinner *ngIf="sending" diameter="18"></mat-spinner>
        <ng-container *ngIf="!sending"><mat-icon>upload</mat-icon> Enviar arquivos</ng-container>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .title { font-weight: 800; font-size: 1.15rem; }
    .muted { color: #6b7280; font-weight: 400; font-size: .95rem; }
    .content { max-width: 680px; }
    .intro { color: #4b5563; margin: 0 0 14px; }
    .drops { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    @media (max-width: 600px) { .drops { grid-template-columns: 1fr; } }
    .drop { border: 2px dashed #cfd6d1; border-radius: 12px; padding: 18px 12px; text-align: center; cursor: pointer; background: #fafbf9; display: flex; flex-direction: column; align-items: center; gap: 4px; color: #4b5563; font-size: .9rem; outline: none; }
    .drop:hover, .drop:focus-visible { border-color: #5daf50; background: #e9f5e6; }
    .drop mat-icon { font-size: 30px; width: 30px; height: 30px; color: #3d8b32; }
    .drop b { color: #1f2a24; }
    .drop.has { border-style: solid; border-color: #5daf50; background: #e9f5e6; }
    .drop.done { border-style: solid; border-color: #9ccf93; }
    .note { display: flex; gap: 8px; align-items: flex-start; border-radius: 8px; padding: 10px 12px; font-size: .85rem; margin-top: 14px; }
    .note.warn { background: #fff3e0; color: #b26a00; }
    .note mat-icon { font-size: 18px; width: 18px; height: 18px; flex: none; }
    .error { color: #c62828; margin-top: 10px; font-size: .9rem; }
    mat-dialog-actions button mat-spinner { display: inline-block; margin-right: 6px; }
  `]
})
export class NfeUploadDialogComponent {
  xml: File | null = null;
  danfe: File | null = null;
  sending = false;
  error = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NfeUploadDialogData,
    private dialogRef: MatDialogRef<NfeUploadDialogComponent, AdminSalesUploadNfeResponse | null>,
    private sales: SalesService,
    private alert: AlertService,
    private cdr: ChangeDetectorRef,
  ) { }

  kb(size: number): string { return (size / 1024).toFixed(0); }

  onPick(ev: Event, kind: 'xml' | 'danfe') {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.setFile(file, kind);
    input.value = '';
  }

  onDrop(ev: DragEvent, kind: 'xml' | 'danfe') {
    ev.preventDefault();
    const file = ev.dataTransfer?.files?.[0] ?? null;
    this.setFile(file, kind);
  }

  private setFile(file: File | null, kind: 'xml' | 'danfe') {
    this.error = '';
    if (!file) return;
    const name = file.name.toLowerCase();
    if (kind === 'xml' && !name.endsWith('.xml')) { this.error = 'A NF-e deve ser um arquivo .xml.'; this.cdr.markForCheck(); return; }
    if (kind === 'danfe' && !name.endsWith('.pdf')) { this.error = 'O DANFE deve ser um arquivo .pdf.'; this.cdr.markForCheck(); return; }
    if (kind === 'xml') this.xml = file; else this.danfe = file;
    this.cdr.markForCheck();
  }

  send() {
    if (!this.xml && !this.danfe) return;
    this.sending = true;
    this.error = '';
    this.sales.uploadNfe(this.data.idKitPayment, this.xml, this.danfe).subscribe({
      next: (res) => {
        this.sending = false;
        if (res.isError) {
          this.error = res.errorDescription || 'Falha ao anexar a NF-e.';
          this.cdr.markForCheck();
          return;
        }
        const parts = [res.xmlSaved ? 'XML' : null, res.danfeSaved ? 'DANFE' : null].filter(Boolean).join(' e ');
        this.alert.show('Sucesso', `${parts} anexado(s). Status: ${res.statusComputed || ''}`, AlertType.success);
        this.dialogRef.close(res);
      },
      error: (err) => {
        this.sending = false;
        this.error = err?.message || 'Erro inesperado ao enviar os arquivos.';
        this.cdr.markForCheck();
      },
    });
  }

  close() { this.dialogRef.close(null); }
}
